import * as styles from "./TaskRow.css";

import { useAtomValue } from "jotai";
import { type JSX, useMemo } from "react";

import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { ListItem } from "@frontend/components/atoms/List";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  describeRecurrence,
  recurrenceLanguage,
  useRecurrenceGetText,
} from "@frontend/components/organisms/DateRecurrencePicker";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import type { ItemAtom } from "@frontend/lib/form";
import { formatMinutes, minutesBetween } from "@frontend/utils/duration";
import { Trans, useLingui } from "@lingui/react/macro";

import { useTaskTypes } from "../hooks/useTaskTypes";
import type { TaskValues } from "../schemas/job";
import { jobForm, referencesAtom } from "../stores/editor";

export type TaskRowProps = {
  index: number;
  itemAtom: ItemAtom;
  onOpen: (origin: HTMLElement) => void;
  onRemove: () => void;
};

/**
 * What a task amounts to, on one row: what the work is, when it runs, and how
 * long. Writing it happens in the popup the row opens.
 */
export function TaskRow({
  index,
  itemAtom,
  onOpen,
  onRemove,
}: TaskRowProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const references = useAtomValue(referencesAtom);
  const errors = useAtomValue(jobForm.state.errors);

  const task = useAtomValue(itemAtom) as TaskValues;

  const gettext = useRecurrenceGetText();

  const language = useMemo(() => recurrenceLanguage(locale), [locale]);

  const spoken = describeRecurrence(task.recurrence.rule, gettext, language);

  // The types of the task's own group: the unfiltered list does not hold them
  // all, so a row read against it would show every task as untyped.
  const taskTypes = useTaskTypes(task.serviceGroupId, references.taskTypes);

  const typeLabel = label(taskTypes, task.typeId);
  const categoryLabel = label(references.categories, task.workCategoryId);
  const groupLabel = label(references.serviceGroups, task.serviceGroupId);

  const ranges = task.hours
    .filter((hour) => hour.begin && hour.end)
    .map((hour) => `${hour.begin}–${hour.end}`);

  const total = task.hours.reduce(
    (sum, hour) => sum + (minutesBetween(hour.begin, hour.end) ?? 0),
    0,
  );

  const incomplete = Object.keys(errors).some((path) =>
    path.startsWith(`tasks.${index}.`),
  );

  const parts = [
    ...(spoken ? [spoken.charAt(0).toUpperCase() + spoken.slice(1)] : []),
    ...(0 < ranges.length ? [ranges.join(", ")] : [t`No hours set`]),
    ...(groupLabel ? [groupLabel] : []),
  ];

  return (
    <ListItem
      value={String(index)}
      onClick={(event) => onOpen(event.currentTarget)}
      startSection={<span className={styles.position}>{index + 1}</span>}
      endSection={
        <span className={styles.end}>
          {0 < total ? (
            <span className={styles.total}>{formatMinutes(total)}</span>
          ) : (
            <span className={styles.noTotal}>&ndash;</span>
          )}

          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={false === task.deletable}
                  aria-label={t`Remove this task`}
                  onClick={(event) => {
                    // The row underneath opens the popup.
                    event.stopPropagation();
                    onRemove();
                  }}
                >
                  <Icon name="Trash2" size="sm" />
                </Button>
              }
            />

            <TooltipContent>
              {false === task.deletable
                ? t`This task carries done or validated hours`
                : t`Remove this task`}
            </TooltipContent>
          </Tooltip>
        </span>
      }
    >
      <span className={styles.body}>
        <span className={styles.head}>
          {typeLabel ? (
            <span className={styles.type}>{typeLabel}</span>
          ) : (
            <span className={styles.untyped}>
              <Trans>Task to fill in</Trans>
            </span>
          )}

          {categoryLabel ? (
            <Badge variant="neutral" size="sm">
              {categoryLabel}
            </Badge>
          ) : null}

          {incomplete ? (
            <Badge variant="warning" size="sm">
              <Trans>Incomplete</Trans>
            </Badge>
          ) : null}
        </span>

        <span className={styles.summary}>{parts.join(" · ")}</span>
      </span>
    </ListItem>
  );
}

function label(
  references: { id: number; label: string }[],
  id: number | null,
): string | null {
  return null === id
    ? null
    : (references.find((reference) => reference.id === id)?.label ?? null);
}
